import pythonApiClient from './pythonBaseApi';

/**
 * Servicio para gestión de turnos
 * Maneja todas las operaciones CRUD relacionadas con turnos
 * Migrado a Python FastAPI - Usa pythonApiClient
 */
export const turnoApi = {
  /**
   * Obtiene turnos detalle con filtros opcionales
   * @param {Object} filters - Filtros opcionales
   * @param {number} filters.idProyecto - ID del proyecto
   * @param {number} filters.idCliente - ID del cliente
   * @param {number} filters.idTurnoCabecera - ID del turno cabecera
   * @param {number} filters.idComedor - ID del comedor
   * @param {number} filters.idComensal - ID del comensal
   * @param {number} filters.idTipoEntrada - ID del tipo de entrada
   * @param {string} filters.valorServicio - Valor del servicio
   * @param {number} filters.idTurno - ID del turno
   * @returns {Promise} - Respuesta de la API
   */
  getAll: (filters = {}) => {
    const params = new URLSearchParams();
    
    Object.keys(filters).forEach(key => {
      if (filters[key] !== null && filters[key] !== undefined) {
        params.append(key, filters[key]);
      }
    });
    
    const endpoint = params.toString() ? `/procesos/alimentacion/turnos-detalle?${params.toString()}` : '/procesos/alimentacion/turnos-detalle';
    return pythonApiClient.get(endpoint);
  },

  /**
   * Obtiene turnos cabecera (turnos totales) - usa el endpoint real del backend
   * @param {Object} filters - Filtros opcionales
   * @param {number} filters.idProyecto - ID del proyecto
   * @param {number} filters.idComedor - ID del comedor
   * @param {number} filters.idTipoServicio - ID del tipo de servicio
   * @param {number} filters.idLector - ID del lector
   * @param {number} filters.cantidadComensales - Cantidad de comensales
   * @param {boolean} filters.cerrado - Estado cerrado
   * @param {number} filters.idTurnoCabecera - ID del turno cabecera
   * @returns {Promise} - Respuesta de la API
   */
  getTurnosTotal: (filters = {}) => {
    // Si hay filtro por proyecto, usar el endpoint específico con todos los filtros en backend
    if (filters.idProyecto) {
      const params = new URLSearchParams();
      
      // Agregar todos los filtros como parámetros de consulta
      Object.keys(filters).forEach(key => {
        if (filters[key] !== null && filters[key] !== undefined && key !== 'idProyecto') {
          params.append(key, filters[key]);
        }
      });
      
      const endpoint = params.toString() 
        ? `/proyectos/${filters.idProyecto}/turnos?${params.toString()}`
        : `/proyectos/${filters.idProyecto}/turnos`;
        
      return pythonApiClient.get(endpoint);
    }
    
    // Si no hay filtro por proyecto, usar el endpoint general (sin filtros adicionales por ahora)
    return pythonApiClient.get('/procesos/alimentacion/turnos-cabecera');
  },

  /**
   * Crea un nuevo turno detalle
   * @param {Object} data - Datos del turno
   * @returns {Promise} - Respuesta de la API
   */
  create: (data) => {
    return pythonApiClient.post('/procesos/alimentacion/turnos-detalle', data);
  },

  /**
   * Actualiza un turno detalle existente
   * @param {number} id - ID del turno
   * @param {Object} data - Datos actualizados del turno
   * @returns {Promise} - Respuesta de la API
   */
  update: (id, data) => {
    return pythonApiClient.put(`/procesos/alimentacion/turnos-detalle/${id}`, data);
  },

  /**
   * Crea un turno cabecera (turno total)
   * @param {Object} data - Datos del turno cabecera
   * @returns {Promise} - Respuesta de la API
   */
  createTurnoTotal: (data) => {
    return pythonApiClient.post('/procesos/alimentacion/turnos-cabecera', data);
  },

  /**
   * Actualiza un turno cabecera (turno total)
   * @param {number} id - ID del turno cabecera
   * @param {Object} data - Datos actualizados del turno cabecera
   * @returns {Promise} - Respuesta de la API
   */
  updateTurnoCabecera: (id, data) => {
    return pythonApiClient.put(`/procesos/alimentacion/turnos-cabecera/${id}`, data);
  },

  /**
   * Elimina un turno cabecera (turno total)
   * @param {number} idTurnoCabecera - ID del turno cabecera
   * @returns {Promise} - Respuesta de la API
   */
  deleteTurnoTotal: (idTurnoCabecera) => {
    return pythonApiClient.delete(`/procesos/alimentacion/turnos-cabecera/${idTurnoCabecera}`);
  },

  /**
   * Carga masiva de turnos detalle desde archivo Excel
   * @param {File} file - Archivo Excel con los datos
   * @param {number} idTurnoCabecera - ID del turno cabecera
   * @returns {Promise} - Respuesta de la API
   */
  bulkUploadTurnoDetalle: (file, idTurnoCabecera, options = {}) => {
    const formData = new FormData();
    formData.append('excel_file', file);
    
    // Solo agregar id_turno_cabecera si tiene un valor válido (no null, no undefined)
    if (idTurnoCabecera !== null && idTurnoCabecera !== undefined) {
      formData.append('id_turno_cabecera', idTurnoCabecera);
    }

    if (options.completarCeros) {
      formData.append('completar_ceros', 'true');
    }
    if (options.forzarCargaParcial) {
      formData.append('forzar_carga_parcial', 'true');
    }
    
    return pythonApiClient.post('/procesos/alimentacion/turnos-detalle/bulk-upload', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
  },

  /**
   * Obtiene errores pendientes de regularización de carga masiva de un turno
   * @param {number} idTurnoCabecera - ID del turno cabecera
   * @returns {Promise}
   */
  getErroresCargaMasiva: (idTurnoCabecera) => {
    return pythonApiClient.get(
      `/procesos/alimentacion/turnos-detalle/${idTurnoCabecera}/errores-carga-masiva`
    );
  },

  /**
   * Lista errores de carga masiva con filtros globales
   */
  listErroresCargaMasiva: (filters = {}) => {
    const params = new URLSearchParams();
    Object.keys(filters).forEach((key) => {
      if (filters[key] !== null && filters[key] !== undefined && filters[key] !== '') {
        params.append(key, filters[key]);
      }
    });
    const qs = params.toString();
    return pythonApiClient.get(
      `/procesos/alimentacion/errores-carga-masiva${qs ? `?${qs}` : ''}`
    );
  },

  /**
   * Actualiza el número de documento de un error pendiente
   */
  updateDocumentoErrorCargaMasiva: (errorId, numeroDocumento) => {
    return pythonApiClient.patch(
      `/procesos/alimentacion/errores-carga-masiva/${errorId}`,
      { numero_documento: numeroDocumento }
    );
  },

  /**
   * Reintenta insertar un error corregido
   */
  reintentarErrorCargaMasiva: (errorId, options = {}) => {
    return pythonApiClient.post(
      `/procesos/alimentacion/errores-carga-masiva/${errorId}/reintentar`,
      { completar_ceros: !!options.completarCeros }
    );
  },

  /**
   * Reintento masivo de errores seleccionados
   */
  reintentarErroresCargaMasivaMasivo: (ids, options = {}) => {
    return pythonApiClient.post(
      '/procesos/alimentacion/errores-carga-masiva/reintentar-masivo',
      { ids, completar_ceros: !!options.completarCeros }
    );
  },

  /**
   * Completa / ajusta la cantidad de comensales del turno
   * usando el turno del día anterior (mismo comedor y servicio).
   */
  completarDetalle: (data) => {
    return pythonApiClient.post(
      '/procesos/alimentacion/turnos-detalle/completar-detalle',
      data
    );
  },

  /**
   * Obtiene solo los números de documento de comensales registrados en un turno
   * Optimizado para validación de duplicados en el modal
   * @param {number} idTurnoCabecera - ID del turno cabecera
   * @returns {Promise} - Respuesta con array de números de documento
   */
  getDocumentosRegistrados: (idTurnoCabecera) => {
    return pythonApiClient.get(`/procesos/alimentacion/turnos-detalle/${idTurnoCabecera}/documentos`);
  },

  /**
   * Obtiene solo los id_comensal registrados en un turno detalle
   * Para cargar en memoria y optimizar validación de duplicados
   * @param {number} idTurnoCabecera - ID del turno cabecera
   * @returns {Promise} - Respuesta con array de id_comensal
   * Obtiene los números de documento de comedor registrados en un turno detalle
   * @param {number} idTurnoCabecera - ID del turno cabecera
   * @returns {Promise} - Respuesta con array de numero_documento_comedor
   */
  getDocumentosRegistradosByTurnoApi: async (idTurnoCabecera) => {
    console.log(' FRONTEND: Iniciando petición getDocumentosRegistrados');
    const startTime = performance.now();
    
    try {
      const response = await pythonApiClient.get(`/procesos/alimentacion/turnos-detalle/${idTurnoCabecera}/documentos-registrados`);
      const endTime = performance.now();
      const duration = endTime - startTime;
      
      console.log(` FRONTEND: getDocumentosRegistrados completado - Duración: ${duration.toFixed(2)}ms - Registros: ${response.data?.length || 0}`);
      
      return response;
    } catch (error) {
      const endTime = performance.now();
      const duration = endTime - startTime;
      
      console.error(` FRONTEND: getDocumentosRegistrados error - Duración: ${duration.toFixed(2)}ms - Error:`, error);
      throw error;
    }
  },

  /**
   * Obtiene los IDs de comensales registrados en un turno
   * @param {number} idTurnoCabecera - ID del turno cabecera
   * @returns {Promise} - Respuesta con array de id_comensal
   */
  getComensalesIds: (idTurnoCabecera) => {
    return pythonApiClient.get(`/procesos/alimentacion/turnos-detalle/${idTurnoCabecera}/comensal-ids`);
  }
};

export default turnoApi;
